/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Card, CardBody, CardHeader, Modal, Button,
  Col, Row, InputGroup,
} from 'reactstrap';
import {
  Formik, Field, Form, ErrorMessage,
  FieldArray, useFormikContext,
} from 'formik';
import * as Yup from 'yup';
import BaseGrid from '../Common/BaseGrid';
import Refund from '../BigCommerce/Refund';
import AmazonRefund from './AmazonRefund';
import { post } from '../../helpers/api_helper';

const REFUND_REASON_OPTIONS = [
  'We sent wrong part',
  'Defective upon receipt',
  'Damaged in shipping',
  'Item not as described',
  'Does not fit / incompatible',
  'Missing parts or accessories',
  'Customer changed mind',
  'Item arrived late',
  'Warranty return',
];

const REFUND_SHORTCUTS = [
  { label: 'Good Unused', reason: 'Customer changed mind' },
  { label: 'Damaged in Shipping', reason: 'Damaged in shipping' },
  { label: 'Warranty Return', reason: 'Warranty return' },
  { label: 'Defective', reason: 'Defective upon receipt' },
];

function roundCurrency(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function calculateLineTotal(saleItem, qty) {
  const paid = Number(saleItem && saleItem.paid);
  const quantity = Number(qty);

  if (!Number.isFinite(paid) || !Number.isFinite(quantity)) {
    return 0;
  }

  return roundCurrency(paid * quantity);
}

function createRefundItem(items) {
  const selectedItem = items.length === 1 ? items[0] : null;
  return {
    sale_item_id: selectedItem ? selectedItem.id : '',
    qty: 1,
    amount: calculateLineTotal(selectedItem, 1),
    reason: '',
  };
}

function RefundAmountSync(props) {
  const { t, manualOverrideRef } = props;
  const { values, setFieldValue } = useFormikContext();
  const previousItemsTotalRef = useRef(null);

  useEffect(() => {
    const itemsTotal = roundCurrency(values.items.reduce((sum, item) => {
      const amount = Number(item.amount);

      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0));

    if (previousItemsTotalRef.current === itemsTotal) {
      return;
    }

    previousItemsTotalRef.current = itemsTotal;

    if (Number(values.refund_amount) !== itemsTotal) {
      if (manualOverrideRef.current) {
        toastr.warning(t('Total Amount was recalculated from the item amounts.'));
      }

      setFieldValue('refund_amount', itemsTotal);
      manualOverrideRef.current = false;
    }
  }, [manualOverrideRef, setFieldValue, t, values.items, values.refund_amount]);

  return null;
}

RefundAmountSync.propTypes = {
  t: PropTypes.func.isRequired,
  manualOverrideRef: PropTypes.shape({
    current: PropTypes.bool,
  }).isRequired,
};

export function SaleRefundsContent(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t, sale, reload } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [signal, setSignal] = useState(0);
  const manualOverrideRef = useRef(false);

  useEffect(() => {
    setSignal(signal + 1);
  }, [reload]);

  const logRefund = async (values) => {
    try {
      await post(`/api/sales/refund/${sale.id}`, values);

      toastr.success(t('Refund logged successfully'));
      manualOverrideRef.current = false;
      setModalOpen(false);
      setSignal(signal + 1);
    } catch (e) {
      toastr.error(t('Error logging refund'));
    }
  };

  const jsx = (
    <>
      <Card>
        <CardHeader className="bg-transparent border-bottom">
          {t('Refunds')}
        </CardHeader>
        <CardBody>
          {sale !== null && (
            <>
              <BaseGrid
                url="/api/sales/refund"
                className="table table-sm table-bordered table-striped"
                filters={`sale_id=${sale.id}`}
                create={() => {
                  manualOverrideRef.current = false;
                  setModalOpen(true);
                }}
                createText={t('Log Refund')}
                shouldUpdate={signal}
                config={
                {
                  columns: {
                    id: {
                      label: t('ID'),
                    },
                    created_at: {
                      label: t('At'),
                      date: {
                        format: (date) => (moment(date).fromNow()),
                        timezone: process.env.LOCAL_TZ,
                      },
                    },
                    refund_amount: {
                      label: t('Amount'),
                    },
                    items: {
                      label: t('Items (qty / amount / reason)'),
                      format: (itms) => itms.map(
                        (itm) => `${itm.sale_item.listing && itm.sale_item.listing.sku} (${itm.qty} / ${itm.amount} / ${itm.reason})`,
                      ).join(', ') || t('Order-level refund'),
                    },
                    reason: {
                      label: t('Comment'),
                    },
                    quickbooks_last_error: {
                      label: t('QuickBooks Error'),
                    },
                  },
                  pagination: {
                    sort: 'id',
                    direction: 'desc',
                    perPage: 30,
                  },
                }
                }
              />

              {sale.platform === 'Rithum' && /^Amazon(?:\b|\.)/i.test(sale.site || '') && (
                <AmazonRefund
                  key={sale.id}
                  sale={sale}
                  t={t}
                  onSubmitted={() => setSignal((current) => current + 1)}
                />
              )}

              {sale.platform === 'BigCommerce' && (
                <div className="mt-1">
                  <Refund reference={sale.reference1} />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Modal
        size="md"
        isOpen={modalOpen}
      >
        <div className="modal-header">
          <h5
            className="modal-title mt-0"
            id="myLargeModalLabel"
          >
            {t('Log Refund')}
          </h5>
          <button
            onClick={() => {
              manualOverrideRef.current = false;
              setModalOpen(false);
            }}
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <Formik
            enableReinitialize
            initialValues={{
              refund_amount: sale?.items?.length === 1 ? calculateLineTotal(sale.items[0], 1) : 0,
              reason: '',
              items: sale?.items?.length === 1 ? [createRefundItem(sale.items)] : [],
            }}
            validationSchema={Yup.object().shape({
              refund_amount: Yup.number().required(t('Amount is required')),
              reason: Yup.string().required(t('Reason is required')),
              items: Yup.array().of(Yup.object().shape({
                sale_item_id: Yup.number().required(t('Item is required')),
                qty: Yup.number().required(t('Qty is required')),
                amount: Yup.number(),
                reason: Yup.string(),
              })),
            })}
            onSubmit={logRefund}
          >
            {({
              touched, errors, values, handleChange, setFieldValue,
            }) => (
              <Form id="RefundLogForm" autoComplete="off">
                <RefundAmountSync t={t} manualOverrideRef={manualOverrideRef} />
                <div className="form-group">
                  <label htmlFor="refund_amount">{t('Total Amount')}</label>
                  <small className="refund-form-instruction d-block mb-2">
                    {t('If line items are entered, this total will be calculated from their line totals.')}
                  </small>
                  <Field
                    type="number"
                    name="refund_amount"
                    id="refund_amount"
                    step="0.01"
                    className={`form-control ${errors.refund_amount && touched.refund_amount ? 'is-invalid' : ''}`}
                    onChange={(e) => {
                      manualOverrideRef.current = true;
                      handleChange(e);
                    }}
                  />
                  <ErrorMessage name="refund_amount" component="div" className="invalid-feedback" />
                </div>

                <FieldArray name="items">
                  {(arrayHelpers) => (
                    <>
                      <div className="hstack gap-3 mt-3">
                        <Button
                          className="btn btn-info btn-sm"
                          onClick={() => {
                            arrayHelpers.push(createRefundItem(sale.items));
                          }}
                        >
                          <span className="bx bx-plus" />
                          {props.t('Add Item')}
                        </Button>
                      </div>
                      <div>
                        {values.items.map((item, idx) => (
                          <div
                            className="bg-warning bg-soft mt-3 refund-form-line-item"
                            key={`item_${idx}`}
                            style={{
                              marginLeft: '-15px',
                              paddingLeft: '15px',
                              marginRight: '-15px',
                              paddingRight: '15px',
                              paddingBottom: '15px',
                              paddingTop: '15px',
                            }}
                          >
                            <Row className="mb-3">
                              <Col sm="12">
                                <label htmlFor={`items[${idx}].sale_item_id`}>{t('Item')}</label>
                                <InputGroup>
                                  <Field
                                    as="select"
                                    name={`items[${idx}].sale_item_id`}
                                    id={`items[${idx}].sale_item_id`}
                                    className={`form-control ${errors.items && errors.items[idx] && errors.items[idx].sale_item_id ? 'is-invalid' : ''}`}
                                    onChange={(e) => {
                                      const saleItemId = e.target.value;
                                      const selectedItem = sale.items.find(
                                        (saleItem) => String(saleItem.id) === saleItemId,
                                      );

                                      setFieldValue(`items[${idx}].sale_item_id`, saleItemId);
                                      setFieldValue(
                                        `items[${idx}].amount`,
                                        calculateLineTotal(selectedItem, item.qty),
                                      );
                                    }}
                                  >
                                    <option value="">{t('Select Item')}</option>
                                    {sale.items.map((itm) => (
                                      <option key={itm.id} value={itm.id}>{itm.listing.sku}</option>
                                    ))}
                                  </Field>
                                  <Button color="danger" onClick={() => arrayHelpers.remove(idx)} className="btn-soft-danger">
                                    <span className="mdi mdi-close-thick" />
                                  </Button>
                                </InputGroup>
                                <ErrorMessage name={`items[${idx}].sale_item_id`} component="div" className="text-danger mt-2" />
                              </Col>
                            </Row>

                            <Row className="mb-3">
                              <Col sm="6">
                                <label htmlFor={`items[${idx}].qty`}>{t('Qty')}</label>
                                <Field
                                  type="number"
                                  name={`items[${idx}].qty`}
                                  id={`items[${idx}].qty`}
                                  className={`form-control ${errors.items && errors.items[idx] && errors.items[idx].qty && touched.items && touched.items[idx] && touched.items[idx].qty ? 'is-invalid' : ''}`}
                                  onChange={(e) => {
                                    const qty = e.target.value;
                                    const selectedItem = sale.items.find(
                                      (saleItem) => String(saleItem.id)
                                        === String(item.sale_item_id),
                                    );

                                    setFieldValue(`items[${idx}].qty`, qty);
                                    setFieldValue(
                                      `items[${idx}].amount`,
                                      calculateLineTotal(selectedItem, qty),
                                    );
                                  }}
                                />
                                <ErrorMessage name={`items[${idx}].qty`} component="div" className="invalid-feedback" />
                              </Col>

                              <Col sm="6">
                                <label htmlFor={`items[${idx}].amount`}>{t('Amount (Line Total)')}</label>
                                <Field
                                  type="number"
                                  name={`items[${idx}].amount`}
                                  id={`items[${idx}].amount`}
                                  step="0.01"
                                  className={`form-control ${errors.items && errors.items[idx] && errors.items[idx].amount && touched.items && touched.items[idx] && touched.items[idx].amount ? 'is-invalid' : ''}`}
                                />
                                <small className="refund-form-instruction refund-form-instruction-inline d-block mt-2">
                                  {t('Enter the total refund amount for this line item, not the per-piece amount.')}
                                </small>
                                <ErrorMessage name={`items[${idx}].amount`} component="div" className="invalid-feedback" />
                              </Col>
                            </Row>

                            {item.sale_item_id && (
                              <div className="d-flex flex-wrap gap-2 mb-3">
                                {REFUND_SHORTCUTS.map((shortcut) => (
                                  <Button
                                    key={shortcut.label}
                                    type="button"
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => {
                                      const reason = t(shortcut.reason);
                                      setFieldValue(`items[${idx}].reason`, reason);
                                      setFieldValue('reason', reason);
                                    }}
                                  >
                                    {t(shortcut.label)}
                                  </Button>
                                ))}
                              </div>
                            )}
                            <div className="form-group">
                              <label htmlFor={`items[${idx}].reason`}>{t('Reason')}</label>
                              <label className="sr-only" htmlFor={`items[${idx}].reason_preset`}>
                                {t('Common refund reason')}
                              </label>
                              <select
                                id={`items[${idx}].reason_preset`}
                                className="form-control mb-2"
                                defaultValue=""
                                onChange={(e) => {
                                  const selectedReason = e.target.value;

                                  if (selectedReason) {
                                    setFieldValue(`items[${idx}].reason`, selectedReason);
                                    e.target.value = '';
                                  }
                                }}
                              >
                                <option value="">{t('Select a common reason')}</option>
                                {REFUND_REASON_OPTIONS.map((option) => (
                                  <option key={option} value={t(option)}>{t(option)}</option>
                                ))}
                              </select>
                              <Field
                                type="text"
                                name={`items[${idx}].reason`}
                                id={`items[${idx}].reason`}
                                placeholder={t('Select a common reason above or type your own')}
                                className={`form-control ${errors.items && errors.items[idx] && errors.items[idx].reason && touched.items && touched.items[idx] && touched.items[idx].reason ? 'is-invalid' : ''}`}
                              />
                              <ErrorMessage name={`items[${idx}].reason`} component="div" className="invalid-feedback" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </FieldArray>

                <div className="form-group mt-3">
                  <label htmlFor="reason">{t('Comment')}</label>
                  <Field
                    type="text"
                    name="reason"
                    id="reason"
                    className={`form-control ${errors.reason && touched.reason ? 'is-invalid' : ''}`}
                  />
                  <ErrorMessage name="reason" component="div" className="invalid-feedback" />
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => {
              manualOverrideRef.current = false;
              setModalOpen(false);
            }}
            className="btn btn-soft-dark"
            data-dismiss="modal"
          >
            {t('Cancel')}
          </button>
          <button
            type="submit"
            form="RefundLogForm"
            className="btn btn-primary"
          >
            {t('Submit')}
          </button>
        </div>
      </Modal>
    </>
  );

  return jsx;
}

SaleRefundsContent.defaultProps = {
  t: (w) => w,
  reload: 0,
};

SaleRefundsContent.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  sale: PropTypes.object.isRequired,
  reload: PropTypes.number,
};

export default connect(() => ({

}), {})(withTranslation()(SaleRefundsContent));
